import type { DataUIPart } from "ai";

import type { AgentUIMessage } from "@/lib/ai/types";

const CHAT_ATTACHMENT_DATA_PART_NAME = "attachment";
const CHAT_ATTACHMENT_DATA_PART_TYPE =
  `data-${CHAT_ATTACHMENT_DATA_PART_NAME}` as const;
export const CHAT_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const CHAT_ATTACHMENT_MAX_COUNT = 4;
/** テキスト添付は本文へ展開するため、ファイル単位で入力量を抑える。 */
export const CHAT_TEXT_ATTACHMENT_MAX_BYTES = 64 * 1024;
export const CHAT_TEXT_BASED_ATTACHMENT_MEDIA_TYPES = [
  "text/csv",
  "text/plain",
  "text/markdown",
  "application/json",
  "application/xml",
  "text/xml",
  "application/yaml",
  "application/x-yaml",
  "text/yaml",
  "application/jsonl",
  "application/x-ndjson",
  "application/toml",
  "application/x-toml",
  "text/html",
  "text/css",
  "application/javascript",
  "text/javascript",
  "text/rtf",
] as const;

/** モデル入力で本文として展開できる、許可済みテキスト添付の MIME 型。 */
export type ChatTextBasedAttachmentMediaType =
  (typeof CHAT_TEXT_BASED_ATTACHMENT_MEDIA_TYPES)[number];
export const CHAT_ATTACHMENT_EXTENSION_MEDIA_TYPES = {
  ".pdf": "application/pdf",
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".webp": "image/webp",
  ".csv": "text/csv",
  ".txt": "text/plain",
  ".log": "text/plain",
  ".md": "text/markdown",
  ".markdown": "text/markdown",
  ".json": "application/json",
  ".xml": "application/xml",
  ".yaml": "application/yaml",
  ".yml": "application/yaml",
  ".jsonl": "application/jsonl",
  ".ndjson": "application/x-ndjson",
  ".toml": "application/toml",
  ".html": "text/html",
  ".htm": "text/html",
  ".css": "text/css",
  ".js": "application/javascript",
  ".mjs": "application/javascript",
  ".cjs": "application/javascript",
  ".rtf": "text/rtf",
} as const;
export const CHAT_ATTACHMENT_MEDIA_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/webp",
  ...CHAT_TEXT_BASED_ATTACHMENT_MEDIA_TYPES,
] as const;

type ChatAttachmentData = {
  filename?: string;
  mediaType: string;
  size?: number;
};

export type ChatAttachmentDataPart = DataUIPart<{
  attachment: ChatAttachmentData;
}>;

export type ChatAttachmentMediaType =
  (typeof CHAT_ATTACHMENT_MEDIA_TYPES)[number];

export function isSupportedChatAttachmentMediaType(
  mediaType: string,
): mediaType is ChatAttachmentMediaType {
  return CHAT_ATTACHMENT_MEDIA_TYPES.some((allowed) => allowed === mediaType);
}

/**
 * ブラウザ報告の MIME 型が未許可なら、許可済み拡張子から補完する。
 *
 * 一部のブラウザや OS はテキスト形式を `application/octet-stream` として返すため、
 * 空文字列だけでなく未許可 MIME 型も拡張子対応表へフォールバックする。
 */
export function resolveChatAttachmentMediaType(
  file: Pick<File, "name" | "type">,
): string {
  if (isSupportedChatAttachmentMediaType(file.type)) return file.type;

  const extension = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
  return (
    CHAT_ATTACHMENT_EXTENSION_MEDIA_TYPES[
      extension as keyof typeof CHAT_ATTACHMENT_EXTENSION_MEDIA_TYPES
    ] ?? ""
  );
}

export function createChatAttachmentDataPart(params: {
  id: string;
  filename?: string;
  mediaType: string;
  size?: number;
}): ChatAttachmentDataPart {
  return {
    type: CHAT_ATTACHMENT_DATA_PART_TYPE,
    id: params.id,
    data: {
      filename: params.filename,
      mediaType: params.mediaType,
      size: params.size,
    },
  };
}

export function isChatAttachmentDataPart(
  part: AgentUIMessage["parts"][number],
): part is ChatAttachmentDataPart {
  return part.type === CHAT_ATTACHMENT_DATA_PART_TYPE;
}

function stripTransientFileParts(message: AgentUIMessage): AgentUIMessage {
  if (!message.parts.some((part) => part.type === "file")) {
    return message;
  }

  return {
    ...message,
    parts: message.parts.filter((part) => part.type !== "file"),
  };
}

export function stripTransientFilePartsFromMessages(
  messages: AgentUIMessage[],
): AgentUIMessage[] {
  return messages.map(stripTransientFileParts);
}
